import { save, SaveEvent, setSaveEventNotifier } from "./Save";
import {
  AchievementDef,
  AchievementEvent,
  ACHIEVEMENT_DEFS,
  TIER_DEFAULTS,
  RunStats,
} from "./achievementDefs";

class AchievementSystem {
  private unlockListeners: ((id: string, def: AchievementDef) => void)[] = [];
  private initialized = false;

  /** Subscribe to unlock events (for toasts / UI refresh). */
  on(fn: (id: string, def: AchievementDef) => void): void {
    this.unlockListeners.push(fn);
  }

  /** Call once at boot to wire up the Save event bridge. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    setSaveEventNotifier((e: SaveEvent) => this.notify(e));
  }

  /** External entry point for events not sourced from Save (e.g. runEnd). */
  notify(e: AchievementEvent): void {
    const data = save.get();
    if (!data.achievements) return; // guard for pre-migration saves
    // Judge against real progress, never the dev campaign override: unlocking
    // carries XP and skill points, so a testing view must not pay out.
    const judged = save.realProgress();

    for (const def of Object.values(ACHIEVEMENT_DEFS)) {
      if (data.achievements.unlocked[def.id]) continue;
      const prev = data.achievements.progress[def.id] ?? 0;
      const result = def.check(e, judged, prev);
      if (result === null) continue;
      if (result === true) {
        this.unlock(def);
        continue;
      }
      if (typeof result === "number" && result > prev) {
        data.achievements.progress[def.id] = result;
        // Don't flush per progress tick; batch with save's existing schedule.
      }
    }
  }

  /** Notify a run-end event with computed stats. Called from GameScene.endGame. */
  notifyRunEnd(stats: RunStats): void {
    this.notify({ kind: "runEnd", stats });
  }

  private unlock(def: AchievementDef): void {
    const data = save.get();
    data.achievements.unlocked[def.id] = Date.now();
    delete data.achievements.progress[def.id];
    this.applyReward(def);
    save.markDirty();
    for (const fn of this.unlockListeners) fn(def.id, def);
  }

  private applyReward(def: AchievementDef): void {
    const defaults = TIER_DEFAULTS[def.tier];
    const xp = def.reward.xp ?? defaults.xp ?? 0;
    const sp = def.reward.skillPoints ?? defaults.skillPoints ?? 0;

    if (xp > 0) save.addXp(xp);
    if (sp > 0) {
      const data = save.get();
      data.skillPoints += sp;
    }
  }
}

export const achievements = new AchievementSystem();
export type { RunStats } from "./achievementDefs";
